import { parse } from 'date-fns'
import {
  RastreioCancerInput,
  RastreioCancerPerguntaDbEnum,
  RastreioCancerPerguntaOpcaoEnum,
  ReferenceMonth,
} from 'graphql/types.generated'
import { dateAsYyyyMmDd, formatDate, formatReferenceMonthAsDdMmYyyy } from 'util/date/formatDate'

import { RASTREIO_CANCER_OPCAO_LABEL } from '../model-rastreioCancer'
import {
  CitopatologicoPerguntaField,
  RequisicaoExameCitopatologicoFormValues,
  SolicitacaoExameCitopatologicoQueryModel,
} from './model-citopatologico'
import { convertDataPreventivoMonthYearToDataPreventivoLocalDate } from './utils-citopatologico'

export const convertCitoFormToRastreioCancer = (
  values: RequisicaoExameCitopatologicoFormValues
): RastreioCancerInput[] => {
  const { checkDum, ...perguntaValues } = values
  return Object.entries(perguntaValues)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(
      ([perguntaKey, value]): RastreioCancerInput => {
        const pergunta = perguntaKey as typeof CitopatologicoPerguntaField[keyof typeof CitopatologicoPerguntaField]
        if (pergunta === CitopatologicoPerguntaField.dataPreventivo) {
          const dataPreventivo = (value as unknown) as ReferenceMonth
          return { pergunta, data: convertDataPreventivoMonthYearToDataPreventivoLocalDate(dataPreventivo) }
        } else if (
          pergunta === CitopatologicoPerguntaField.dum ||
          pergunta === CitopatologicoPerguntaField.dataDaColeta
        ) {
          return { pergunta, data: value }
        } else if (
          pergunta === CitopatologicoPerguntaField.nomeResponsavel ||
          pergunta === CitopatologicoPerguntaField.cns ||
          pergunta === CitopatologicoPerguntaField.numeroConselho ||
          pergunta === CitopatologicoPerguntaField.siglaNomeConselho
        ) {
          return { pergunta, descricao: value }
        } else {
          return { pergunta, opcoes: [value as RastreioCancerPerguntaOpcaoEnum] }
        }
      }
    )
}

const RASTREIO_CANCER_DESCRICAO_TO_OPCAO: Record<string, RastreioCancerPerguntaOpcaoEnum> = Object.entries(
  RASTREIO_CANCER_OPCAO_LABEL
).reduce((acc, [opcao, label]) => {
  acc[label.toLowerCase()] = opcao as RastreioCancerPerguntaOpcaoEnum
  return acc
}, {} as Record<string, RastreioCancerPerguntaOpcaoEnum>)

const parseRespostaDdMmYyyy = (resposta: string): Date => parse(resposta, 'dd/MM/yyyy', new Date())

const convertRespostaToCitoFormValue = (
  pergunta: RastreioCancerPerguntaDbEnum,
  resposta: string
): RastreioCancerPerguntaOpcaoEnum | string => {
  if (pergunta === CitopatologicoPerguntaField.dataPreventivo) {
    const data = parseRespostaDdMmYyyy(resposta)
    const dataPreventivo: ReferenceMonth = { month: data.getMonth(), year: data.getFullYear() }
    return (dataPreventivo as unknown) as string
  } else if (pergunta === CitopatologicoPerguntaField.dum || pergunta === CitopatologicoPerguntaField.dataDaColeta) {
    return dateAsYyyyMmDd(parseRespostaDdMmYyyy(resposta))
  } else if (
    pergunta === CitopatologicoPerguntaField.nomeResponsavel ||
    pergunta === CitopatologicoPerguntaField.cns ||
    pergunta === CitopatologicoPerguntaField.numeroConselho ||
    pergunta === CitopatologicoPerguntaField.siglaNomeConselho
  ) {
    return resposta
  } else {
    return RASTREIO_CANCER_DESCRICAO_TO_OPCAO[resposta.toLowerCase()]
  }
}

export const convertSolicitacaoCitopatologicoToCitoForm = (
  solicitacaoCitopatologico: SolicitacaoExameCitopatologicoQueryModel
): RequisicaoExameCitopatologicoFormValues =>
  solicitacaoCitopatologico.questoes.reduce((acc, questao) => {
    if (questao) {
      acc[questao.pergunta] = convertRespostaToCitoFormValue(questao.pergunta, questao.resposta)
    }
    return acc
  }, {} as RequisicaoExameCitopatologicoFormValues)

export function convertRequisicaoExameCitopatologicoFormToMap(
  values: RequisicaoExameCitopatologicoFormValues
): Map<RastreioCancerPerguntaDbEnum, string> {
  const { checkDum, ...perguntaValues } = values

  return new Map(
    Object.entries(perguntaValues)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([perguntaKey, value]) => {
        const pergunta = perguntaKey as typeof CitopatologicoPerguntaField[keyof typeof CitopatologicoPerguntaField]

        if (pergunta === CitopatologicoPerguntaField.dataPreventivo) {
          const dataPreventivo = (value as unknown) as ReferenceMonth
          return [pergunta, formatReferenceMonthAsDdMmYyyy(dataPreventivo) as LocalDate]
        } else if (
          pergunta === CitopatologicoPerguntaField.dum ||
          pergunta === CitopatologicoPerguntaField.dataDaColeta
        ) {
          return [pergunta, formatDate(value)]
        } else if (
          pergunta === CitopatologicoPerguntaField.nomeResponsavel ||
          pergunta === CitopatologicoPerguntaField.cns ||
          pergunta === CitopatologicoPerguntaField.numeroConselho ||
          pergunta === CitopatologicoPerguntaField.siglaNomeConselho
        ) {
          return [pergunta, value]
        } else {
          return [pergunta, RASTREIO_CANCER_OPCAO_LABEL[value as RastreioCancerPerguntaOpcaoEnum]]
        }
      })
  )
}
